<template>
   <b-container v-if="loaded">
      <b-row class="title-row mt-3 mb-4">
         <b-col class="d-flex align-items-center justify-content-between">
            <h1 class="d-inline-block mb-0" style="text-transform: none">
               {{ company.companyName }}
            </h1>

            <div class="d-flex align-items-center">
               <b-button
                  v-if="isAdmin"
                  class="mr-2"
                  :to="{name: 'admin-company', params: {id: $companyId}}"
               >
                  Admin View
               </b-button>
               <b-button
                  id="btn-import-data"
                  variant="outline-primary"
                  class="mr-2"
                  :to="{name: 'company-import', params: {id: $companyId}}"
               >
                  Import Data
               </b-button>
               <b-button
                  id="btn-export-data"
                  variant="primary"
                  :to="{name: 'company-export', params: {id: $companyId}}"
               >
                  Generate deliverable
               </b-button>
            </div>
         </b-col>
      </b-row>

      <b-alert variant="warning" :show="!hasCompanyRole && hasStaff">
         Some actions are unavailable to users not assigned to this company.
      </b-alert>

      <b-alert variant="danger" :show="!hasStaff">
         This company has no assigned staff. This will limit some functionality for Customer and SME
         users associated with this company.
      </b-alert>

      <CompanyInformation />

      <ProjectsCard :hasCompanyRole="hasCompanyRole" @deleteQuestionnaire="deleteQuestionnaire" />

      <ActivitiesCard />

      <CopyConfigCard />

      <UploadsCard :hasCompanyRole="hasCompanyRole" />

      <StaffRolesCard
         :hasCompanyRole="hasCompanyRole"
         :businessManagers="businessManagers"
         :projectManagers="projectManagers"
         :projectAssociates="projectAssociates"
         :externalPartners="externalPartners"
      />

      <CompanyUserCard />

      <StudyCard :hasCompanyRole="hasCompanyRole" />

      <TaxReturnCard v-if="activeStudyId" />

      <EmployeesCard v-if="activeStudyId" />

      <CompanyQuestionnaireCard v-if="activeStudyId" @deleteQuestionnaire="deleteQuestionnaire" />

      <ProjectQuestionnaireCard v-if="activeStudyId" @deleteQuestionnaire="deleteQuestionnaire" />

      <EmployeesTimeCard v-if="activeStudyId" />

      <ContractorsCard v-if="activeStudyId" :noBusinessComponent="hasNoProjectPeriods" />

      <SuppliesCard v-if="activeStudyId" :noBusinessComponent="hasNoProjectPeriods" />

      <CloudCard v-if="activeStudyId" :noBusinessComponent="hasNoProjectPeriods" />

      <CreditCalculationCard v-if="activeStudyId" />

      <FlagsCard v-if="activeStudyId" />
   </b-container>
</template>

<script>
import {mapGetters} from 'vuex';

import ErrorCodes from '@/helpers/errorCodes';

import CompanyInformation from './widgets/CompanyInformation';
import StudyCard from './widgets/StudyCard';
import EmployeesCard from './widgets/EmployeesCard';
import ActivitiesCard from './widgets/ActivitiesCard';
import ProjectsCard from './widgets/ProjectsCard';
import CompanyQuestionnaireCard from './widgets/CompanyQuestionnaireCard';
import ProjectQuestionnaireCard from './widgets/ProjectQuestionnaireCard';
import EmployeesTimeCard from './widgets/EmployeesTimeCard';
import ContractorsCard from './widgets/ContractorsCard';
import SuppliesCard from './widgets/SuppliesCard';
import CloudCard from './widgets/CloudCard';
import CopyConfigCard from './widgets/CopyConfigCard';
import StaffRolesCard from './widgets/StaffRolesCard';
import CompanyUserCard from './widgets/CompanyUserCard';
import UploadsCard from './widgets/UploadsCard.vue';
import FlagsCard from './widgets/FlagsCard';
import CreditCalculationCard from './widgets/CreditCalculationCard.vue';
import TaxReturnCard from '@/views/company/widgets/TaxReturnCard.vue';

export default {
   name: 'CompanyDetail',

   components: {
      TaxReturnCard,
      CompanyInformation,
      StudyCard,
      EmployeesCard,
      ActivitiesCard,
      ProjectsCard,
      CompanyQuestionnaireCard,
      ProjectQuestionnaireCard,
      EmployeesTimeCard,
      ContractorsCard,
      SuppliesCard,
      CloudCard,
      CopyConfigCard,
      StaffRolesCard,
      CompanyUserCard,
      UploadsCard,
      FlagsCard,
      CreditCalculationCard,
   },

   async created() {
      this.$store.commit('legacyEmployees/clear');
      this.$store.commit('activities/clear');
      this.$store.commit('projects/clear');
      this.$store.commit('flags/clear');

      await this.fetchData();
      this.loaded = true;
   },

   data() {
      return {
         loaded: false,
         restrictedDataLoadPromiseKey: null,
         restrictedDataLoadPromise: null,
      };
   },

   computed: {
      ...mapGetters({
         isAdmin: 'isAdmin',
         profile: 'profile',
         staffUsers: 'users/staffUsers',
         externalPartnerUsers: 'users/externalPartnerUsers',
         company: 'companies/currentCompany',
         hasOpenStudy: 'companies/hasOpenStudy',
         activeStudyId: 'companies/activeStudyId',
         projectsSupportedPeriodIds: 'projects/projectsSupportedPeriodIds',
      }),

      /** True when this company has no R&D business components (projects) in an open study. */
      hasNoProjectPeriods() {
         return this.projectsSupportedPeriodIds.length === 0;
      },

      // The staff accounts assigned to this company as business managers
      businessManagers() {
         return this.staffUsers.filter((user) => {
            return this.company.businessManagers.includes(user.id);
         });
      },

      // The staff accounts assigned to this company as project managers
      projectManagers() {
         return this.staffUsers.filter((user) => {
            return this.company.projectManagers.includes(user.id);
         });
      },

      // The staff accounts assigned to this company as project associates
      projectAssociates() {
         return this.staffUsers.filter((user) => {
            return this.company.projectAssociates.includes(user.id);
         });
      },

      // The staff accounts assigned to this company as project external partners
      externalPartners() {
         return this.externalPartnerUsers.filter((user) => {
            return this.company.externalPartners.includes(user.id);
         });
      },

      /** Can the current user act on this company? Staff users must have a role. */
      hasCompanyRole() {
         if (null === this.company) {
            return false;
         }
         const id = this.profile.id;
         if (this.isAdmin) {
            return true;
         } else if (
            this.businessManagers.map((user) => user.id).includes(id) ||
            this.projectManagers.map((user) => user.id).includes(id) ||
            this.projectAssociates.map((user) => user.id).includes(id) ||
            this.externalPartners.map((user) => user.id).includes(id)
         ) {
            return true;
         } else {
            return false;
         }
      },

      /** True if any staff members have an assigned role with this company */
      hasStaff() {
         return (
            this.businessManagers.length > 0 ||
            this.projectManagers.length > 0 ||
            this.projectAssociates.length > 0 ||
            this.externalPartners.length > 0
         );
      },
   },

   methods: {
      async fetchData() {
         const requests = [
            // fetch industries
            this.$store.dispatch('industries/loadIndustries'),

            // fetch users
            this.$store.dispatch('users/loadUsers', {enabledOnly: true}),
         ];

         if (this.hasCompanyRole) {
            requests.push(this.fetchRestrictedData());
         }

         await this.loadPage(requests);
      },

      // Fetch data restricted to staff associated with this company
      async fetchRestrictedData() {
         if (!this.hasCompanyRole) {
            return;
         }

         const loadKey = `${this.$companyId}:${this.activeStudyId ?? 'none'}`;
         if (this.restrictedDataLoadPromise && this.restrictedDataLoadPromiseKey === loadKey) {
            return this.restrictedDataLoadPromise;
         }

         const requests = [];
         if (this.activeStudyId) {
            // fetch company questionnaires
            requests.push(
               this.$store
                  .dispatch('questionnaire/loadQuestionnaires', {companyId: this.$companyId})
                  .catch((err) => {
                     this.$store.commit('showAlert', {
                        response: err.response,
                        fallbackMsg: 'Failed to load questionnaires',
                        seconds: 5,
                     });
                  })
            );
         }

         // Fetch studies
         requests.push(this.$store.dispatch('companies/loadStudies', {companyId: this.$companyId}));

         // fetch upload categories
         requests.push(
            this.$store.dispatch('uploads/loadCompanyUploadCategories', {
               companyId: this.$companyId,
               summary: true,
               force: true,
            })
         );

         requests.push(this.$store.dispatch('projects/loadProjects', {companyId: this.$companyId}));

         // fetch activities
         requests.push(
            this.$store.dispatch('activities/loadActivities', {companyId: this.$companyId})
         );

         this.restrictedDataLoadPromiseKey = loadKey;
         this.restrictedDataLoadPromise = Promise.allSettled(requests).finally(() => {
            this.restrictedDataLoadPromise = null;
            this.restrictedDataLoadPromiseKey = null;
         });

         return await this.restrictedDataLoadPromise;
      },

      /** Send the initial delete request to determine if this object can be deleted */
      async deleteQuestionnaire(questionnaire) {
         try {
            await this.blockingRequest('questionnaire/deleteQuestionnaire', {
               questionnaireId: questionnaire.id,
            });
            const proceed = await this.$bvModal.msgBoxConfirm(
               `Are you sure you want to delete questionnaire "${questionnaire.title}"?`,
               {
                  id: 'modal-delete-questionnaire',
                  title: 'Delete Questionnaire?',
                  centered: true,
                  okTitle: 'Delete',
                  okVariant: 'danger',
               }
            );
            if (proceed) {
               await this.blockingRequest('questionnaire/deleteQuestionnaire', {
                  questionnaireId: questionnaire.id,
                  force: true,
               });
            }
         } catch (err) {
            const errCode = err.response.data.errors[0].code;
            if (errCode === ErrorCodes.CANNOT_DELETE) {
               this.$bvModal.msgBoxOk(
                  'The selected questionnaire has related data, and cannot be deleted by a staff user. Contact an admin user to delete this questionnaire.',
                  {
                     title: 'Questionnaire Has Related Data',
                     centered: true,
                  }
               );
            }
         }
      },
   },

   watch: {
      hasCompanyRole(value) {
         if (value) {
            this.fetchRestrictedData();
         }
      },

      activeStudyId() {
         this.fetchRestrictedData();
      },
   },
};
</script>
